import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Briefcase, Plus, Download, Check, AlertTriangle, Trash2, Mail,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const FORMATS = [
  ["continu", "Service continu sur la journée"],
  ["deux_services", "Deux services, midi et soir"],
  ["unique", "Un service groupé"],
];
const STATUTS = {
  preparation: ["En préparation", "bg-amber-100 text-amber-900"],
  envoye: ["Envoyée", "bg-blue-100 text-blue-900"],
  gagne: ["Gagnée", "bg-green-100 text-green-900"],
  perdu: ["Perdue", "bg-muted text-muted-foreground"],
};

const SECTIONS = [
  ["collectif", "Qui sommes-nous"],
  ["bar", "Le bar café"],
  ["logistique", "Logistique et besoins techniques"],
  ["charte", "La charte RSE"],
];
const POINTS = [
  ["date", "Confirmation de la date"],
  ["horaires", "Les horaires"],
  ["facturation", "Le mode de facturation"],
  ["implantation", "Le plan d'implantation"],
];

const VIDE = {
  client: "", lieu: "", date_evenement: "", convives: 500,
  heures_service: 6, format_service: "continu", camions: [],
  bar: true, bubble_tea: true, taux_prise: 80,
  sections: SECTIONS.map((x) => x[0]), points: POINTS.map((x) => x[0]),
  zones_bar: ["Paddock", "Zone food trucks"], statut: "preparation",
};

export default function AffairesPage() {
  const [affaires, setAffaires] = useState([]);
  const [camions, setCamions] = useState([]);
  const [nouvelle, setNouvelle] = useState(null);
  const [controle, setControle] = useState({});
  const [enCours, setEnCours] = useState("");
  const [blocs, setBlocs] = useState([]);
  const [textesOuverts, setTextesOuverts] = useState("");
  const [brouillon, setBrouillon] = useState({});

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/affaires`),
        axios.get(`${API}/dossiers/camions`),
      ]);
      setAffaires(a.data.affaires || []);
      setCamions(c.data.camions || []);
      try {
        const b = await axios.get(`${API}/dossier-blocs`);
        setBlocs(b.data.blocs || []);
      } catch { setBlocs([]); }
    } catch {
      toast.error("Chargement impossible");
    }
  };

  const creer = async () => {
    if (!nouvelle.client.trim())
      return toast.error("Le nom du client est obligatoire");
    if (!nouvelle.camions.length)
      return toast.error("Désignez au moins un camion");
    try {
      await axios.post(`${API}/affaires`, {
        ...nouvelle,
        convives: parseInt(nouvelle.convives, 10) || 0,
        heures_service: parseFloat(nouvelle.heures_service) || 6,
        taux_prise: parseInt(nouvelle.taux_prise, 10) || 80,
      });
      toast.success("Affaire créée, les camions sont sollicités");
      setNouvelle(null);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    }
  };

  const ouvrirTextes = (a) => {
    if (textesOuverts === a.id) return setTextesOuverts("");
    setBrouillon(a.textes || {});
    setTextesOuverts(a.id);
  };

  const enregistrerTextes = async (a) => {
    try {
      // On ne garde que ce qui est renseigne : un champ vide signifie
      // « reprends le texte par defaut », pas « pas de texte ».
      const propres = {};
      Object.entries(brouillon).forEach(([k, v]) => {
        if (v && v.trim()) propres[k] = v.trim();
      });
      await axios.put(`${API}/affaires/${a.id}`, { ...a, textes: propres });
      toast.success("Textes enregistrés pour cette affaire");
      setTextesOuverts("");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const verifier = async (a) => {
    try {
      const r = await axios.post(`${API}/dossiers/verifier`, {
        client: a.client, lieu: a.lieu, date_evenement: a.date_evenement,
        convives: a.convives, heures_service: a.heures_service,
        format_service: a.format_service, camions: a.camions,
        taux_prise: a.taux_prise,
      });
      setControle((c) => ({ ...c, [a.id]: r.data }));
    } catch {
      toast.error("Vérification impossible");
    }
  };

  const generer = async (a) => {
    setEnCours(a.id);
    try {
      const r = await axios.post(`${API}/affaires/${a.id}/pdf`, {},
                                 { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = `dossier-${a.client.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      lien.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier généré");
      charger();
    } catch {
      toast.error("Génération impossible");
    } finally {
      setEnCours("");
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'affaire ${a.client} ?`)) return;
    try {
      await axios.delete(`${API}/affaires/${a.id}`);
      toast.success("Affaire supprimée");
      charger();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const changerStatut = async (a, statut) => {
    try {
      await axios.put(`${API}/affaires/${a.id}`, { ...a, statut });
      charger();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const Statut = ({ a }) => {
    const [lib, cls] = STATUTS[a.statut] || STATUTS.preparation;
    return (
      <select
        className={`text-xs rounded-md px-2 py-1 border-0 ${cls}`}
        value={a.statut || "preparation"}
        onChange={(e) => changerStatut(a, e.target.value)}
      >
        {Object.entries(STATUTS).map(([k, v]) => (
          <option key={k} value={k}>{v[0]}</option>
        ))}
      </select>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6" /> Appels d'offres
          </h1>
          <p className="text-sm text-muted-foreground">
            Créez l'affaire, désignez les camions : chacun reçoit la demande
            de menu dans son espace.
          </p>
        </div>
        {!nouvelle && (
          <Button onClick={() => setNouvelle({ ...VIDE })}>
            <Plus className="h-4 w-4 mr-1" /> Nouvelle affaire
          </Button>
        )}
      </div>

      {nouvelle && (
        <Card className="border-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Nouvelle affaire</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[["client", "Client", "ex. Volvo"],
                ["lieu", "Lieu", "ex. Autodrome de Linas-Montlhéry"]]
                .map(([k, lib, ph]) => (
                <div key={k} className="space-y-1">
                  <label className="text-xs text-muted-foreground">{lib}</label>
                  <Input placeholder={ph} value={nouvelle[k]}
                         onChange={(e) =>
                           setNouvelle({ ...nouvelle, [k]: e.target.value })} />
                </div>
              ))}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Date</label>
                <Input type="date" value={nouvelle.date_evenement}
                       onChange={(e) => setNouvelle({
                         ...nouvelle, date_evenement: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Convives
                </label>
                <Input type="number" min="1" value={nouvelle.convives}
                       onChange={(e) => setNouvelle({
                         ...nouvelle, convives: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Format de service
                </label>
                <select className="w-full border rounded-md h-9 px-2 text-sm"
                        value={nouvelle.format_service}
                        onChange={(e) => setNouvelle({
                          ...nouvelle, format_service: e.target.value })}>
                  {FORMATS.map(([v, lib]) => (
                    <option key={v} value={v}>{lib}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Heures de service
                </label>
                <Input type="number" step="0.5" min="1" max="14"
                       value={nouvelle.heures_service}
                       onChange={(e) => setNouvelle({
                         ...nouvelle, heures_service: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Prise de repas (%)
                </label>
                <Input type="number" min="10" max="100" step="5"
                       value={nouvelle.taux_prise}
                       onChange={(e) => setNouvelle({
                         ...nouvelle, taux_prise: e.target.value })} />
                <p className="text-xs text-muted-foreground">
                  Part des présents qui déjeuneront sur place.
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Camions sollicités — {nouvelle.camions.length} retenu
                {nouvelle.camions.length > 1 ? "s" : ""}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-56 overflow-auto">
                {camions.map((c) => (
                  <label key={c.id}
                         className="flex gap-2 items-start border rounded-md p-2 cursor-pointer hover:bg-muted/40 text-sm">
                    <input type="checkbox" className="mt-1"
                           checked={nouvelle.camions.includes(c.id)}
                           onChange={() => setNouvelle({
                             ...nouvelle,
                             camions: nouvelle.camions.includes(c.id)
                               ? nouvelle.camions.filter((x) => x !== c.id)
                               : [...nouvelle.camions, c.id],
                           })} />
                    <span className="flex-1">
                      <span className="font-medium">{c.nom}</span>
                      {!c.pret && (
                        <span className="block text-xs text-amber-700">
                          fiche incomplète : {c.manque.join(", ")}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex gap-2 items-center text-sm">
              <input type="checkbox" checked={nouvelle.bar}
                     onChange={(e) => setNouvelle({
                       ...nouvelle, bar: e.target.checked })} />
              Inclure le bar café et viennoiseries
            </label>

            {nouvelle.bar && (
              <label className="flex gap-2 items-center text-sm pl-6">
                <input type="checkbox" checked={nouvelle.bubble_tea}
                       onChange={(e) => setNouvelle({
                         ...nouvelle, bubble_tea: e.target.checked })} />
                Proposer aussi le bubble tea
              </label>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Sections du dossier
                </p>
                {SECTIONS.map(([code, lib]) => (
                  <label key={code}
                         className="flex gap-2 items-center text-sm py-0.5">
                    <input type="checkbox"
                           checked={nouvelle.sections.includes(code)}
                           onChange={() => setNouvelle({
                             ...nouvelle,
                             sections: nouvelle.sections.includes(code)
                               ? nouvelle.sections.filter((x) => x !== code)
                               : [...nouvelle.sections, code],
                           })} />
                    {lib}
                  </label>
                ))}
                <p className="text-xs text-muted-foreground mt-1">
                  La couverture, l'offre et les fiches camions sont
                  toujours présentes.
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Points à arrêter, en dernière page
                </p>
                {POINTS.map(([code, lib]) => (
                  <label key={code}
                         className="flex gap-2 items-center text-sm py-0.5">
                    <input type="checkbox"
                           checked={nouvelle.points.includes(code)}
                           onChange={() => setNouvelle({
                             ...nouvelle,
                             points: nouvelle.points.includes(code)
                               ? nouvelle.points.filter((x) => x !== code)
                               : [...nouvelle.points, code],
                           })} />
                    {lib}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setNouvelle(null)}>
                Annuler
              </Button>
              <Button onClick={creer}>Créer et solliciter</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {affaires.map((a) => {
        const ctrl = controle[a.id];
        const complet = a.menus_rendus === a.menus_attendus;
        return (
          <Card key={a.id}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base">{a.client}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {[a.lieu, a.date_evenement,
                      a.convives ? `${a.convives} convives` : ""]
                      .filter(Boolean).join(" · ")}
                  </p>
                </div>
                <Statut a={a} />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-muted/40 rounded-md p-2">
                  <p className="text-xs text-muted-foreground">Menus rendus</p>
                  <p className="text-xl font-semibold">
                    {a.menus_rendus} / {a.menus_attendus}
                  </p>
                </div>
                <div className="bg-muted/40 rounded-md p-2">
                  <p className="text-xs text-muted-foreground">Camions</p>
                  <p className="text-xl font-semibold">
                    {a.camions?.length || 0}
                  </p>
                </div>
                <div className="bg-muted/40 rounded-md p-2">
                  <p className="text-xs text-muted-foreground">Format</p>
                  <p className="text-sm pt-1">
                    {(FORMATS.find((f) => f[0] === a.format_service)
                      || ["", "—"])[1].split(",")[0]}
                  </p>
                </div>
              </div>

              <div>
                {(a.camions_detail || []).map((c) => (
                  <div key={c.id}
                       className="flex justify-between items-start gap-2 border-b py-1.5">
                    <span>
                      {c.nom}
                      {!!(c.manque || []).length && (
                        <span className="block text-xs text-amber-700">
                          manque : {c.manque.join(", ")}
                        </span>
                      )}
                    </span>
                    {c.menu_depose ? (
                      <span className="text-green-700 flex items-center gap-1 text-xs">
                        <Check className="h-3.5 w-3.5" />
                        menu reçu le{" "}
                        {new Date(c.depose_le).toLocaleDateString("fr-FR")}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-amber-700">
                          menu attendu
                        </span>
                        <Button
                          variant="ghost" size="sm" className="h-7 text-xs"
                          onClick={() => toast.info(
                            `Relance à envoyer à ${c.nom} — le courriel ` +
                            `automatique n'est pas encore branché.`)}>
                          <Mail className="h-3.5 w-3.5 mr-1" /> Relancer
                        </Button>
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {ctrl && (
                <div className={`rounded-md p-2 text-xs ${
                  ctrl.alerte ? "bg-amber-50 text-amber-900" : "bg-muted/40"}`}>
                  <p>{ctrl.capacite}</p>
                  {ctrl.alerte && (
                    <p className="flex gap-1 mt-1">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      {ctrl.alerte}
                    </p>
                  )}
                </div>
              )}

              {!complet && (
                <p className="text-xs text-muted-foreground">
                  Vous pouvez générer dès maintenant : les camions sans menu
                  auront simplement une page en moins.
                </p>
              )}
              {textesOuverts === a.id && (
                <div className="border rounded-md p-3 space-y-4 bg-muted/20">
                  <p className="text-xs text-muted-foreground">
                    Ces textes ne valent que pour cette affaire. Laissez un
                    champ vide pour reprendre le texte par défaut.
                  </p>
                  {blocs.map((b) => (
                    <div key={b.id} className="space-y-1">
                      <p className="text-sm font-medium">{b.title}</p>
                      {b.aide && (
                        <p className="text-xs text-muted-foreground">
                          {b.aide}
                        </p>
                      )}
                      <textarea
                        className="w-full border rounded-md p-2 text-sm"
                        rows={3}
                        placeholder={b.content}
                        value={brouillon[b.id] || ""}
                        onChange={(e) =>
                          setBrouillon({ ...brouillon, [b.id]: e.target.value })
                        }
                      />
                      <div className="flex gap-1 flex-wrap">
                        {(b.variantes || []).map((v, i) => (
                          <button
                            key={i}
                            type="button"
                            className="text-xs border rounded-md px-2 py-1 hover:bg-white"
                            onClick={() =>
                              setBrouillon({ ...brouillon, [b.id]: v.content })
                            }
                          >
                            {v.titre}
                          </button>
                        ))}
                        {brouillon[b.id] && (
                          <button
                            type="button"
                            className="text-xs underline text-muted-foreground px-1"
                            onClick={() =>
                              setBrouillon({ ...brouillon, [b.id]: "" })
                            }
                          >
                            Revenir au défaut
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm"
                            onClick={() => setTextesOuverts("")}>
                      Fermer
                    </Button>
                    <Button size="sm" onClick={() => enregistrerTextes(a)}>
                      Enregistrer les textes
                    </Button>
                  </div>
                </div>
              )}


              <div className="flex gap-2 justify-end flex-wrap">
                <Button variant="ghost" size="sm"
                        onClick={() => supprimer(a)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => ouvrirTextes(a)}>
                  {textesOuverts === a.id ? "Masquer les textes"
                    : "Textes du dossier"}
                  {a.textes && Object.keys(a.textes).length > 0
                    ? ` (${Object.keys(a.textes).length})` : ""}
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => verifier(a)}>
                  Vérifier
                </Button>
                <Button size="sm" disabled={enCours === a.id}
                        onClick={() => generer(a)}>
                  <Download className="h-4 w-4 mr-1" />
                  {enCours === a.id ? "Génération…" : "Générer le dossier"}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {!affaires.length && !nouvelle && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Aucune affaire pour le moment. Créez-en une : les camions que
            vous désignerez recevront la demande de menu dans leur espace.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
